import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import {
  GET_MEMBERSHIP_DETAILS_EDUCATION_QUERY,
  GET_MEMBERSHIP_DETAILS_QUERY,
  GET_PROSPECT_PROGRAM_QUERY
} from "../person/queries";
import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import {
  BackgroundCheck,
  EligibleEmployment,
  Person,
  PlayingAbilityTestStatus,
  QualifyingLevel,
  ProgramStatus as PersonProgramStatus,
  ProgramTypeName
} from "@/types";
import Table, { buildActionColumn } from "@/components/Table";
import type { Action } from "@/components/MenuButton";
import { toUTCFormat } from "@/lib/dateHelpers";
import { scrollToElement } from "@/lib/scrollHelpers";
import { capitalize } from "lodash";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import ProgramStatus, { Status } from "../person/_components/ProgramStatus";
import { formatStatus, hasMeaningfulPATStatus } from "@/features/person/utils/utils";
import { ACTIVITY_TYPES } from "../person/_components/constants";
import ErrorBoundary from "@/components/ErrorBoundary";
import { calculatePATProgramStatus, getPATSuspensionDetails } from "@/features/person/utils/pat";
import PATActivityTable from "@/features/person/components/PATActivityTable";
import { BackgroundCheckDispositionModal } from "@/features/person/components/BackgroundCheckDisposition";
import { canEditBackgroundCheckDispositionOnProspectProgram } from "@/features/person/components/BackgroundCheckOrders/backgroundCheckEligibility";
import { getInterestFormTableConfig } from "../person/_activities/interest-form";
import { ActivityTableConfig, ActivityRow } from "../person/_activities/types";
import { DOMAIN, PERMISSION, useCanManageBackgroundCheck, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { sanitizeHtmlId } from "@/lib/htmlHelpers";
import { usePersonMembershipAccess } from "../person/_usePersonMembershipAccess";

type PersonData = { person: Person };

const PAT_ACTIVITY_SCROLL_ID = "pat-activity-table";

type ActivityType = QualifyingLevel | PlayingAbilityTestStatus | BackgroundCheck | EligibleEmployment;

const createBaseActivity = (activity: string, activityType: ActivityType | null | undefined, completed: boolean) => {
  const { status, startDate, endDate, expirationDate } = activityType || {};
  return {
    id: activity,
    activity,
    status: formatStatus(status, "Not Started"),
    date: startDate ? toUTCFormat(startDate) : endDate ? toUTCFormat(endDate) : null,
    details: null,
    expiredAt: expirationDate && toUTCFormat(expirationDate),
    lineItems: [],
    programStatus: (completed ? "Complete" : "Incomplete") as Status
  };
};

const createPgaAccountActivity = (accountCreatedAt?: string) => ({
  id: "pga-account",
  activity: ACTIVITY_TYPES.PGA_ACCOUNT,
  status: "Complete",
  date: toUTCFormat(accountCreatedAt || ""),
  details: null,
  expiredAt: null,
  lineItems: [],
  completed: true,
  programStatus: "Complete" as Status
});

const createPATStatusActivity = (
  pat: PlayingAbilityTestStatus,
  pgmProgramHistory: Array<{ level: number; startDate: string; pgmProgramExpirationDate?: string | null }> = [],
  hasReInstatementBand: boolean,
  latestProgram?: { status: PersonProgramStatus; programType?: { name?: string }; band?: { terminatesOn?: string } }
) => {
  const { status, startDate, endDate, expirationDate, expired, completed } = pat || {};
  const programStatus = calculatePATProgramStatus(pat);
  const isMembershipTerminated =
    latestProgram?.programType?.name === ProgramTypeName.Member &&
    latestProgram?.status === PersonProgramStatus.TERMINATED;

  let details = getPATSuspensionDetails(pat);
  if (expired) {
    const currentDate = new Date().toISOString().split("T")[0];
    const level1Program = pgmProgramHistory.find((pgm) => pgm.level === 1);
    const level1StartDate = level1Program?.startDate;
    const pgmProgramExpirationDate = pgmProgramHistory.find(
      (pgm) => pgm.pgmProgramExpirationDate
    )?.pgmProgramExpirationDate;

    if (!level1StartDate && !isMembershipTerminated) {
      details = "PAT expired before PGM Level 1 registration";
    } else if (isMembershipTerminated && hasReInstatementBand) {
      details = "PAT expired after membership termination, reelection required.";
    } else if (pgmProgramExpirationDate && pgmProgramExpirationDate < currentDate) {
      details = "PAT expired after associate program termination without acceptable progress";
    }
  }

  return {
    id: ACTIVITY_TYPES.PAT,
    activity: ACTIVITY_TYPES.PAT,
    status: status || expired ? capitalize(expired ? "Expired" : status) : undefined,
    date: startDate ? toUTCFormat(startDate) : endDate ? toUTCFormat(endDate) : null,
    details: details,
    expiredAt: expirationDate && toUTCFormat(expirationDate),
    lineItems: [],
    completed: completed,
    programStatus: programStatus as Status
  };
};

const getActivityTableConfig = (activityType: string): ActivityTableConfig | null => {
  switch (activityType) {
    case ACTIVITY_TYPES.INTEREST_FORM:
      return getInterestFormTableConfig();
    default:
      return null;
  }
};

export default function ProspectProgramPage() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonMembership } = usePersonMembershipAccess();
  const canViewPersonEducation = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonEducation
  });
  const [searchParams] = useSearchParams();
  const flags = useFlags();
  const canManageBackgroundCheck = useCanManageBackgroundCheck();
  const canEditBackgroundCheckDisposition = canEditBackgroundCheckDispositionOnProspectProgram({
    flags,
    canManageBackgroundCheck
  });
  const [editBackgroundCheckOpen, setEditBackgroundCheckOpen] = useState(false);
  const { Menu: RowMenu, setMenu } = useTableRowMenu();

  const skipMembershipQueries = !loaded || !canViewPersonMembership || !pgaId;

  const { data, loading, error } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries
  });

  const { data: educationData, loading: educationLoading } = useQuery<PersonData>(
    GET_MEMBERSHIP_DETAILS_EDUCATION_QUERY,
    {
      variables: { pgaId },
      fetchPolicy: "cache-first",
      skip: skipMembershipQueries || !canViewPersonEducation
    }
  );

  const {
    data: qualifyingLevelData,
    loading: qualifyingLevelLoading,
    error: qualifyingLevelError
  } = useQuery<PersonData>(GET_PROSPECT_PROGRAM_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries
  });

  const person = {
    ...(data?.person || ({} as Person)),
    ...(educationData?.person ?? {})
  };
  const { bands, latestProgram } = data?.person ?? {};

  const prospectProgram = qualifyingLevelData?.person?.prospectProgram;
  const accountCreatedAt = qualifyingLevelData?.person?.accountCreatedAt;
  const hasReInstatementBand = bands?.some((band) => band.createdReason === "re_instatement") ?? false;

  const renderTableDetailsPanel = React.useCallback((row: { original: ActivityRow }) => {
    const activityType = row.original.activity;
    const config = getActivityTableConfig(activityType);

    if (!config) {
      return <></>;
    }

    return (
      <TableDetailsPanel
        data={row.original.lineItems}
        columns={config.columns}
        onClickRow={config.onClickRow}
        getRowClasses={config.getRowClasses}
        title={`${activityType} (${row.original.lineItems.length})`}
      />
    );
  }, []);

  const prospectProgramTableData = useMemo(() => {
    const employmentStatus = prospectProgram?.eligibleEmployment?.id ? "Complete" : "Not Started";
    const pgmHistory = person.educationPgmProgramHistory || [];

    if (!prospectProgram) return [];

    const hasInterestForm = !!prospectProgram.membershipInterestForm;
    const rows: ActivityRow[] = [];

    if (hasInterestForm || accountCreatedAt) {
      rows.push(createPgaAccountActivity(accountCreatedAt));
    }

    if (hasInterestForm) {
      rows.push({
        id: "pga-membership-interest-form",
        activity: ACTIVITY_TYPES.INTEREST_FORM,
        status: "Complete",
        date: toUTCFormat(prospectProgram.membershipInterestForm!.startedAt),
        completed: true,
        expiredAt: null,
        lineItems: [
          {
            id: "reason-for-interest",
            item: "Reason For Interest",
            answer: prospectProgram.membershipInterestForm!.reasonForBecomingMember
          }
        ],
        programStatus: "Complete" as Status
      });
    }

    if (hasInterestForm || prospectProgram.eligibleEmployment) {
      rows.push(
        createBaseActivity(
          ACTIVITY_TYPES.ELIGIBLE_EMPLOYMENT,
          { ...prospectProgram.eligibleEmployment, status: employmentStatus },
          !!prospectProgram.eligibleEmployment?.id
        )
      );
    }

    if (hasInterestForm || prospectProgram.backgroundCheck) {
      rows.push(
        createBaseActivity(
          ACTIVITY_TYPES.BACKGROUND_CHECK,
          prospectProgram.backgroundCheck,
          prospectProgram.backgroundCheck?.status === "APPROVED"
        )
      );
    }

    if (hasInterestForm || prospectProgram.qualifyingLevel) {
      rows.push(
        createBaseActivity(
          ACTIVITY_TYPES.QUALIFYING_LEVEL,
          prospectProgram.qualifyingLevel,
          !!prospectProgram.qualifyingLevel?.completed
        )
      );
    }

    if (
      hasInterestForm ||
      latestProgram?.programType?.name ||
      hasMeaningfulPATStatus(prospectProgram.playingAbilityTestStatus)
    ) {
      rows.push(
        createPATStatusActivity(
          prospectProgram.playingAbilityTestStatus,
          pgmHistory,
          hasReInstatementBand,
          latestProgram
        )
      );
    }

    if (hasInterestForm || prospectProgram.latestAssociateApplication) {
      rows.push(
        createBaseActivity(
          ACTIVITY_TYPES.ASSOCIATE_APPLICATION,
          prospectProgram.latestAssociateApplication,
          ["Approved", "Elected"].includes(prospectProgram.latestAssociateApplication?.status || "")
        )
      );
    }

    return rows;
  }, [prospectProgram, accountCreatedAt, person.educationPgmProgramHistory, hasReInstatementBand, latestProgram]);

  const isLoading = qualifyingLevelLoading || loading || (canViewPersonEducation && educationLoading);

  useEffect(() => {
    const scrollTo = searchParams.get("scrollTo");

    if (scrollTo === PAT_ACTIVITY_SCROLL_ID && !isLoading) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("scrollTo");
      window.history.replaceState({}, "", currentUrl.toString());

      requestAnimationFrame(() => {
        scrollToElement(PAT_ACTIVITY_SCROLL_ID);
      });
    }
  }, [searchParams, isLoading]);

  if (!loaded || !canViewPersonMembership) {
    return null;
  }

  if (error || qualifyingLevelError) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Prospect Program"
        message={error?.message || qualifyingLevelError?.message}
      />
    );
  }

  return (
    <div>
      {(isLoading || !!prospectProgramTableData.length) && (
        <div>
          <div className="pt-12" />
          <Table
            data={prospectProgramTableData}
            loading={isLoading}
            getRowCanExpand={(row) => row.original.lineItems?.length > 0}
            renderExpandedRow={({ row }) => (!!row.original.lineItems.length ? renderTableDetailsPanel(row) : <></>)}
            columns={[
              {
                accessorKey: "activity",
                header: "Activity",
                size: 250,
                enableSorting: false,
                meta: { headerClassName: "hide-header-in-column" }
              },
              {
                accessorKey: "status",
                header: "Stage",
                id: "status",
                enableSorting: false
              },
              {
                accessorKey: "details",
                header: "Details",
                id: "details",
                enableSorting: false,
                size: 250
              },
              {
                accessorKey: "date",
                header: "Date",
                id: "date",
                enableSorting: false
              },
              {
                accessorKey: "expiredAt",
                header: "Expiration",
                id: "expiredAt",
                enableSorting: false
              },
              {
                accessorKey: "programStatus",
                header: "Program Status",
                id: "programStatus",
                enableSorting: false,
                meta: {
                  headerClassName: "hide-header-in-column"
                },
                cell: ({ row }) => <ProgramStatus status={row.original.programStatus} />
              },
              ...(canEditBackgroundCheckDisposition
                ? [
                    buildActionColumn<ActivityRow>({
                      getActions: (row) => {
                        const rowActions: Action[] = [];
                        if (row.activity === ACTIVITY_TYPES.BACKGROUND_CHECK) {
                          rowActions.push({
                            text: "Edit Background Check",
                            onClick: () => setEditBackgroundCheckOpen(true)
                          });
                        }
                        return rowActions;
                      },
                      actionId,
                      setMenu,
                      getRowId: (row) => sanitizeHtmlId(row.id)
                    })
                  ]
                : [])
            ]}
            data-testid="prospect-program-table"
          />
          {canEditBackgroundCheckDisposition && <RowMenu />}
        </div>
      )}
      {canEditBackgroundCheckDisposition && pgaId && (
        <BackgroundCheckDispositionModal
          open={editBackgroundCheckOpen}
          onClose={() => setEditBackgroundCheckOpen(false)}
          pgaId={pgaId}
          backgroundCheck={prospectProgram?.backgroundCheck}
        />
      )}
      <div id={PAT_ACTIVITY_SCROLL_ID}>
        <PATActivityTable
          patStatus={prospectProgram?.playingAbilityTestStatus}
          loading={qualifyingLevelLoading}
          enableNavigation={true}
          pgaId={pgaId}
          personId={person.id}
        />
      </div>
    </div>
  );
}
